import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import useGameBase from '../../hooks/useGameBase';

const COLOR_SWATCHES = ['red', 'yellow', 'green', 'blue'];

function getParticipantId(player) {
  return player?.socketId || player?.userId || player?.id || null;
}

function getSeatPosition(index, total) {
  if (total <= 1) {
    return { left: 50, top: 14 };
  }

  const angle = (-Math.PI / 2) + ((Math.PI * 2 * index) / total);
  const radiusX = 40;
  const radiusY = 26;

  return {
    left: 50 + (Math.cos(angle) * radiusX),
    top: 50 + (Math.sin(angle) * radiusY),
  };
}

function createSeatConnector(fromSeat, toSeat, stemLength = 4, headLength = 2.6, headWidth = 1.8) {
  const dx = toSeat.left - fromSeat.left;
  const dy = toSeat.top - fromSeat.top;
  const distance = Math.hypot(dx, dy) || 1;
  const ux = dx / distance;
  const uy = dy / distance;
  const px = -uy;
  const py = ux;
  const midX = (fromSeat.left + toSeat.left) / 2;
  const midY = (fromSeat.top + toSeat.top) / 2;
  const arrowTipX = midX + (ux * (stemLength / 2));
  const arrowTipY = midY + (uy * (stemLength / 2));
  const stemStartX = midX - (ux * (stemLength / 2));
  const stemStartY = midY - (uy * (stemLength / 2));
  const headBaseX = arrowTipX - (ux * headLength);
  const headBaseY = arrowTipY - (uy * headLength);

  return {
    x1: stemStartX,
    y1: stemStartY,
    x2: arrowTipX,
    y2: arrowTipY,
    headPoints: [
      `${arrowTipX},${arrowTipY}`,
      `${headBaseX + (px * headWidth)},${headBaseY + (py * headWidth)}`,
      `${headBaseX - (px * headWidth)},${headBaseY - (py * headWidth)}`,
    ].join(' '),
  };
}

function WanGame({ gameState = {}, gamePlayers = [], user = {}, onGameAction, socket, roomId }) {
  const { submitAction } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const myId = user?.socketId || user?.userId;
  const myHand = gameState?.playerHands?.[myId] || [];
  const topDiscard = (gameState?.discardPile || []).slice(-1)[0];
  const isMyTurn = gameState.currentPlayer === myId;

  const cardRefs = useRef({});
  const discardRef = useRef(null);
  const deckRef = useRef(null);
  const prevTopRef = useRef(null);
  const preloadedImagesRef = useRef({});
  const [animating, setAnimating] = useState(null); // { key, style, card }
  const [wildPicker, setWildPicker] = useState(null); // { index, rect }
  const [wanPressed, setWanPressed] = useState(false);

  // Clear local pressed state when server broadcasts a call or target changes
  useEffect(() => {
    if (gameState.wanFirstCaller || !gameState.wanTarget) {
      setWanPressed(false);
    }
  }, [gameState.wanFirstCaller, gameState.wanTarget]);

  function handleWanClick() {
    if (wanPressed) return;
    setWanPressed(true);
    // send action to server
  submitAction({ type: 'wan' }).catch(() => setTimeout(() => setWanPressed(false), 1000));
    // As optimistic UI: hide the button locally immediately. Server will broadcast to others.
  }

  useEffect(() => {
    // clear anim when state changes such that top discard changes (server accepted play)
    if (!animating) return;
    const newTop = (gameState?.discardPile || []).slice(-1)[0];
    if (newTop && animating.card && newTop.id === animating.card.id) {
      // server acknowledged played card
      setTimeout(() => setAnimating(null), 300);
    }
  }, [gameState.discardPile, animating]);

  // clear saved previous-top when animation completes/cleared
  useEffect(() => {
    if (!animating) prevTopRef.current = null;
  }, [animating]);

  // Preload all card images once to avoid flicker when they are first shown
  useEffect(() => {
    const colors = ['Red', 'Yellow', 'Green', 'Blue'];
    const srcs = new Set();
  const thumbSuffix = '_thumb';

    // Number cards 0-9
    for (const c of colors) {
      for (let v = 0; v <= 9; v++) {
        srcs.add(`/cards/wan/${c}_${v}${thumbSuffix}.png`);
      }
      // Action cards
      srcs.add(`/cards/wan/${c}_Draw_2${thumbSuffix}.png`);
      srcs.add(`/cards/wan/${c}_Skip${thumbSuffix}.png`);
      srcs.add(`/cards/wan/${c}_Reverse${thumbSuffix}.png`);
    }
    // Wilds
    srcs.add(`/cards/wan/Wild${thumbSuffix}.png`);
    srcs.add(`/cards/wan/Wild_Draw_4${thumbSuffix}.png`);

    const images = [];
    srcs.forEach((s) => {
      try {
        const img = new Image();
        img.src = s;
        preloadedImagesRef.current[s] = img;
        images.push(img);
      } catch (e) {
        // ignore load errors — missing assets will still fallback to browser 404
      }
    });

    return () => {
      // optional cleanup: drop references so GC can reclaim
      images.forEach((img) => { try { img.src = ''; } catch (e) {} });
      preloadedImagesRef.current = {};
    };
  }, []);

  function isPlayable(card) {
  if (!card) return false;
  // If there's a pending draw penalty (draw2/draw4) the player cannot play any card
  if (gameState && gameState.pendingDraw && gameState.pendingDraw > 0) return false;
  if (card.type === 'wild') return true;
  if (card.color && card.color === gameState.currentColor) return true;
  // allow matching value 0: use explicit undefined checks
  if (typeof card.value !== 'undefined' && typeof gameState.currentValue !== 'undefined' && card.value === gameState.currentValue) return true;
  return false;
  }

  function hasColorMatchInHand(color) {
    return myHand.some(c => c.color && c.color === color);
  }

  function playCard(index, chosenColor = null) {
    const card = myHand[index];
    if (!card) return;

    // find DOM rects
    const cardEl = cardRefs.current[card.id];
    const discardEl = discardRef.current;
    if (cardEl && discardEl) {
      const start = cardEl.getBoundingClientRect();
      const end = discardEl.getBoundingClientRect();
      const overlayStyle = {
        left: start.left + window.scrollX,
        top: start.top + window.scrollY,
        width: start.width,
        height: start.height,
      };
  // save current top-discard so UI can keep showing it until animation finishes
  prevTopRef.current = topDiscard;
      setAnimating({ key: `play-${card.id}-${Date.now()}`, style: overlayStyle, card });
      // trigger move on next tick
      requestAnimationFrame(() => {
        setAnimating((a) => a ? { ...a, style: { ...a.style, left: end.left + window.scrollX, top: end.top + window.scrollY, width: end.width, height: end.height, transition: 'all 360ms ease' } } : null);
      });
    }

    // send action to server (optimistic)
    const action = { type: 'play', index };
    if (chosenColor) action.color = chosenColor;
  submitAction && submitAction(action).catch(()=>{});

    // clear wild picker if any
    setWildPicker(null);
    // clear anim after a short delay (keeps overlay while state updates)
    setTimeout(() => setAnimating(null), 500);
  }

  function onCardClick(card, idx, e) {
    if (!isMyTurn) return;
    if (!isPlayable(card)) return;
  // If the very first card on the discard pile is a wild, allow the first player to choose a colour

    // If this card is a wild, enforce wild4 restriction (client-side) before showing picker
    if (card.type === 'wild') {
      if (card.value === 'wild4') {
        // allow only if no color match
        if (hasColorMatchInHand(gameState.currentColor)) return;
      }
      // show color picker positioned near card
      const rect = e.currentTarget.getBoundingClientRect();
      setWildPicker({ index: idx, rect });
      return;
    }

  // NOTE: do not show color picker for initial wilds; only wild cards should show picker

    playCard(idx, null);
  }

  function onPickWildColor(color) {
    if (!wildPicker) return;
    playCard(wildPicker.index, color);
  }

  const findPlayerName = useCallback((id) => {
    if (!id) return null;
    const p = (gamePlayers || []).find(x => x.socketId === id || x.userId === id) || {};
    return p.displayName || p.name || id;
  }, [gamePlayers]);

  function capitalize(s) {
    if (!s) return '';
    return String(s).charAt(0).toUpperCase() + String(s).slice(1).toLowerCase();
  }

  function getCardImage(card) {
    // return the thumbnail variant (append _thumb before extension)
    const thumbSuffix = '_thumb';
    if (!card) return `/cards/wan/Wild${thumbSuffix}.png`;
    if (card.type === 'number') {
      return `/cards/wan/${capitalize(card.color)}_${card.value}${thumbSuffix}.png`;
    }
    if (card.type === 'action') {
      if (card.value === 'draw2') return `/cards/wan/${capitalize(card.color)}_Draw_2${thumbSuffix}.png`;
      return `/cards/wan/${capitalize(card.color)}_${capitalize(card.value)}${thumbSuffix}.png`;
    }
    if (card.type === 'wild') {
      if (card.value === 'wild4') return `/cards/wan/Wild_Draw_4${thumbSuffix}.png`;
      return `/cards/wan/Wild${thumbSuffix}.png`;
    }
    return `/cards/wan/Wild${thumbSuffix}.png`;
  }

  // Back image for the deck (thumbnail variant)
  function getBackImage() {
  // Use the standard card back image from the cards folder
  return `/cards/card_back.png`;
  }

  const tablePlayers = useMemo(() => {
    return (gamePlayers || []).map((player, index) => {
      const playerId = getParticipantId(player);
      const handCount = playerId && gameState?.playerHands?.[playerId]
        ? gameState.playerHands[playerId].length
        : 0;
      const displayName = player.displayName || player.name || player.username || player.userName || findPlayerName(playerId) || playerId;

      return {
        id: playerId || `seat-${index}`,
        displayName,
        handCount,
        isCurrentTurn: playerId === gameState.currentPlayer,
        isLocalPlayer: !!playerId && playerId === myId,
        seat: getSeatPosition(index, Math.max((gamePlayers || []).length, 1)),
      };
    });
  }, [findPlayerName, gamePlayers, gameState.currentPlayer, gameState.playerHands, myId]);

  const tableConnections = useMemo(() => {
    if (tablePlayers.length < 2) return [];

    const step = gameState.direction === -1 ? -1 : 1;
    return tablePlayers.map((player, index) => {
      const nextIndex = (index + step + tablePlayers.length) % tablePlayers.length;
      const nextPlayer = tablePlayers[nextIndex];
      return {
        key: `${player.id}-${nextPlayer.id}`,
        ...createSeatConnector(player.seat, nextPlayer.seat),
      };
    });
  }, [gameState.direction, tablePlayers]);

  const directionLabel = gameState.direction === -1 ? 'Counter-clockwise' : 'Clockwise';
  const directionGlyph = gameState.direction === -1 ? '↺' : '↻';

  return (
    <div className="w-full p-3">
      <div className="mb-4 overflow-hidden rounded-3xl border border-slate-200/80 bg-gradient-to-br from-slate-50 to-slate-100 px-3 py-4 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:to-slate-800">
        <div className="mb-3 flex items-center justify-between gap-3 text-xs uppercase tracking-[0.22em] text-slate-500 dark:text-slate-300">
          <span>Table Layout</span>
          <span className="flex items-center gap-2 font-semibold text-sky-700 dark:text-yellow-300">
            <span className="text-base leading-none">{directionGlyph}</span>
            <span>{directionLabel}</span>
          </span>
        </div>

        <div className="relative mx-auto h-56 max-w-xl">
          <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            <ellipse cx="50" cy="50" rx="34" ry="22" className="fill-emerald-100/80 stroke-emerald-300 dark:fill-emerald-950/40 dark:stroke-emerald-800" strokeWidth="1.5" />
            {tableConnections.map((connection) => (
              <g key={connection.key} className="text-sky-500 dark:text-yellow-300" opacity="0.95">
                <line
                  x1={connection.x1}
                  y1={connection.y1}
                  x2={connection.x2}
                  y2={connection.y2}
                  stroke="currentColor"
                  strokeWidth="1.1"
                  strokeLinecap="round"
                />
                <polygon points={connection.headPoints} fill="currentColor" />
              </g>
            ))}
          </svg>

          {/* <div className="absolute inset-[20%_26%] flex items-center justify-center rounded-[999px] border border-dashed border-slate-300/80 bg-white/55 text-center text-xs font-medium text-slate-600 shadow-sm backdrop-blur dark:border-slate-600/80 dark:bg-slate-900/45 dark:text-slate-200">
            <div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-slate-400 dark:text-slate-400">Turn</div>
              <div className="mt-1 text-sm font-semibold text-slate-700 dark:text-slate-100">{findPlayerName(gameState.currentPlayer) || 'Waiting'}</div>
            </div>
          </div> */}

          {tablePlayers.map((player) => (
            <div
              key={player.id}
              className="absolute -translate-x-1/2 -translate-y-1/2"
              style={{ left: `${player.seat.left}%`, top: `${player.seat.top}%` }}
            >
              <div
                className={`min-w-[88px] max-w-[108px] rounded-2xl border px-2 py-2 text-center shadow-sm transition-colors ${player.isCurrentTurn ? 'border-amber-400 bg-amber-100 text-amber-950 dark:border-amber-500 dark:bg-amber-500/15 dark:text-amber-100' : 'border-slate-200 bg-white/90 text-slate-700 dark:border-slate-700 dark:bg-slate-900/80 dark:text-slate-100'}`}
              >
                <div className="truncate text-xs font-semibold">
                  {player.displayName}{player.isLocalPlayer ? ' (You)' : ''}
                </div>
                <div className="mt-1 text-[11px] text-slate-500 dark:text-slate-300">{player.handCount} card{player.handCount === 1 ? '' : 's'}</div>
                {player.isCurrentTurn && (
                  <div className="mt-1 text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700 dark:text-amber-300">Active</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>


      <div className="flex items-center gap-4 mb-3">
        <div className="flex items-center gap-2">
          <div ref={deckRef} className={`relative w-20 ${isMyTurn ? 'card-wobble cursor-pointer' : ''}`}
            onClick={isMyTurn ? (() => submitAction && submitAction({ type: 'draw' }).catch(()=>{})) : undefined}
            style={{ pointerEvents: isMyTurn ? 'auto' : 'none' }}>
            {/* Single deck image to match player cards; centered white count label */}
            <img src={getBackImage()} alt="deck-back" className="w-full rounded-2xl shadow-md block" style={{ objectFit: 'cover' }} />
            {/* Draw pile count pill overlay, styled like Go Fish */}
            <div className="absolute -bottom-2 -right-2 px-2 py-0.5 bg-black text-white text-xs rounded-full shadow" style={{ minWidth: 24, textAlign: 'center', fontWeight: 600 }}>
              {(gameState.drawPile || []).length}
            </div>
            {/* No draw button, deck itself is clickable and wobbles when it's your turn */}
          </div>
          <div style={{ width: 8 }} />
          <div ref={discardRef} className="w-20 rounded-2xl shadow flex items-center justify-center bg-white dark:bg-gray-700 overflow-hidden">
            {(() => {
              // Decide what to show on the discard pile.
              // If an animation is active, prefer the previously saved top card so the
              // played card isn't visible beneath the flying overlay.
              const realTop = (gameState?.discardPile || []).slice(-1)[0];
              const displayed = animating ? (prevTopRef.current || realTop) : realTop;
              if (displayed) {
                return <img src={getCardImage(displayed)} alt="top-discard" style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }} />;
              }
              return <span className="text-gray-700 dark:text-gray-300">Empty</span>;
            })()}
          </div>
          {/* colour indicator rendered to the right of the discard so it isn't cropped */}
          {topDiscard && topDiscard.type === 'wild' && gameState.currentColor && (
            <div aria-hidden style={{ width: 18, height: 18, borderRadius: 9999, border: '2px solid rgba(255,255,255,0.9)', backgroundColor: gameState.currentColor, marginLeft: 6 }} />
          )}
        </div>
        {/* <div className="flex-1">
          <div className="text-sm text-gray-700 dark:text-gray-200">Current colour: <span style={{ display: 'inline-block', width: 18, height: 12, backgroundColor: gameState.currentColor || '#ccc', border: '1px solid rgba(0,0,0,0.2)', marginLeft: 8 }} /></div>
          <div className="text-xs text-gray-500">{isMyTurn ? 'Your turn' : `Turn: ${gameState.currentPlayer || 'N/A'}`}</div>
        </div> */}
        <div className="flex flex-col items-end gap-1">
          {/* Wan call UI */}
          {gameState.wanTarget && !gameState.finished && (
            <div className="text-right">
              {/* <div className="text-xs text-yellow-600 dark:text-yellow-300">Target: {findPlayerName(gameState.wanTarget)}</div> */}
              <div className="mt-1">
                {!gameState.wanFirstCaller ? (
                  // hide the button for everyone if someone has already called (gameState.wanFirstCaller set)
                  !wanPressed && (
                    <button onClick={handleWanClick} className="px-3 py-1 bg-yellow-500 dark:bg-yellow-400 text-black rounded font-semibold">Wan!</button>
                  )
                ) : (
                  <div className="text-xs text-gray-600 dark:text-gray-300">Called by: {findPlayerName(gameState.wanFirstCaller)}</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

  <div className="mb-2 text-gray-800 dark:text-gray-200">Your Hand ({myHand.length})</div>
      <style>{`
        @keyframes wobbleCard { 0% { transform: rotate(2deg) scale(1.00); } 25% { transform: rotate(0deg) scale(1.00); } 50% { transform: rotate(-2deg) scale(1.00); } 75% { transform: rotate(0deg) scale(1.00); }  100% { transform: rotate(2deg) scale(1.0); } }
        .card-wobble { animation: wobbleCard 1.2s infinite; }
      `}</style>
      <div className="flex gap-3 flex-wrap items-end">
        {myHand.map((card, idx) => {
          const playable = isPlayable(card) && isMyTurn;
          const imgSrc = getCardImage(card);
          return (
            <div
              key={card.id}
              ref={el => { if (el) cardRefs.current[card.id] = el; }}
              onClick={(e) => onCardClick(card, idx, e)}
              className={`w-20 rounded-2xl shadow-md overflow-hidden cursor-pointer select-none ${playable ? 'card-wobble' : ''}`}
              title={card.type === 'number' ? `${card.color} ${card.value}` : `${card.type} ${card.value}`}
            >
              <img src={imgSrc} alt={card.type === 'number' ? `${card.color} ${card.value}` : `${card.value}`} style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block' }} />
            </div>
          );
        })}
      </div>

  <div className="mt-4" />

      {/* Wild color picker popover */}
      {wildPicker && (
        <div style={{ position: 'fixed', left: wildPicker.rect.left, top: wildPicker.rect.bottom + 6, zIndex: 2000 }}>
          <div className="bg-white dark:bg-gray-800 p-2 rounded shadow flex gap-2">
            {COLOR_SWATCHES.map((c) => (
              <button key={c} onClick={() => onPickWildColor(c)} className="w-8 h-6 rounded" style={{ backgroundColor: c, border: '1px solid rgba(0,0,0,0.2)' }} />
            ))}
            <button onClick={() => setWildPicker(null)} className="ml-2 px-2 py-1 border rounded">Cancel</button>
          </div>
        </div>
      )}

      {/* Animated overlay for played card */}
      {animating && (
        <div style={{ position: 'fixed', left: animating.style.left, top: animating.style.top, width: animating.style.width, height: animating.style.height, zIndex: 3000, transition: animating.style.transition || 'none' }}>
          <img src={getCardImage(animating.card)} alt="anim-card" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', boxShadow: '0 6px 18px rgba(0,0,0,0.3)', borderRadius: 8 }} />
        </div>
      )}
    </div>
  );
}

export default WanGame;
